import React from 'react';
import PropTypes from 'prop-types';
import { observer } from 'mobx-react';
import SweetAlert from 'react-bootstrap-sweetalert';

import { AddressStore } from '../../stores/AddressStore';
import { SessionStore } from '../../stores/SessionStore';
import { LocationStore } from '../../stores/LocationStore';
import { ConfigParametersStore } from '../../stores/ConfigParametersStore';
import { AddressController } from '../../networking/controllers/AddressController';
import { BasicInput } from '../basic-input';
import { Checkbox } from '../checkbox';
import { SecondaryButton } from '../secondary-button';
import { PrimaryButton } from '../primary-button';
import { BaseButton } from '../base-button';
import { Select } from '../select';
import { ERROR_CODES, ERROR_CODES_MESSAGE } from '../../models/ApiError';
import { ADDRESS_TYPES } from '../../models/Address';
import { AddressHelper } from '../../helpers/AddressHelper';
import { enforceDepartments } from '../../hocs/enforce-departments';
import { StreetAutocompleteInput } from '../street-autocomplete-input';
import { Modal } from '../modal';
import { SimpleButton } from '../simple-button';
import { Spinner } from '../spinner';
import styles from './AddressForm.css';

const IPropTypes = {
  collapseEarly: PropTypes.bool,
  onAddressCreation: PropTypes.func,
  onAddressEdition: PropTypes.func,
  reference: PropTypes.func,
  withoutTitle: PropTypes.bool,
  address: PropTypes.shape({
    id: PropTypes.string,
    addressType: PropTypes.string,
    streetName: PropTypes.string,
    additionalData: PropTypes.string,
    deliveryDisabled: PropTypes.bool,
    firstCorner: PropTypes.string,
    secondCorner: PropTypes.string,
    doorNumber: PropTypes.string,
    cityName: PropTypes.shape({
      id: PropTypes.number,
      name: PropTypes.string,
    }),
    department: PropTypes.shape({
      id: PropTypes.number,
      name: PropTypes.string,
    }),
    bis: PropTypes.bool,
    apartment: PropTypes.string,
    letter: PropTypes.string,
    extraInfo1: PropTypes.shape({
      value: PropTypes.string,
      field: PropTypes.shape({
        id: PropTypes.number,
        label: PropTypes.string,
      }),
    }),
    extraInfo2: PropTypes.shape({
      value: PropTypes.string,
      field: PropTypes.shape({
        id: PropTypes.number,
        label: PropTypes.string,
      }),
    }),
  }),
};

const defaultProps = {
  collapseEarly: false,
  onAddressCreation: () => {},
  onAddressEdition: () => {},
  reference: () => {},
  withoutTitle: false,
  address: null,
};

const BLOCK_PARAM_ID = 380;
const COORDINATES_NUDGE_VALUE = 1e-5;
const MAX_APARTMENT_LENGTH = 6;
const MAX_EXTRA_INFO_LENGTH = 5;
const MAX_LETTER_LENGTH = 1;
const MAX_STREET_LENGTH = 150;
const MINIMUM_COORDINATE_DIFFERENCE = 1e-10;
const MONTEVIDEO_ID = 1;

const ADDRESS_TYPE_OPTIONS = Object.values(ADDRESS_TYPES).map(addressType => ({
  label: AddressHelper.getAddressType(addressType),
  value: addressType,
}));

class AddressForm extends React.Component {
  constructor(props) {
    super(props);
    const address = props.address || {};
    const { extraInfo1 } = address;
    const { extraInfo2 } = address;
    const extraInfoField1 = (extraInfo1 && extraInfo1.field) ? extraInfo1.field : null;
    const extraInfoField2 = (extraInfo2 && extraInfo2.field) ? extraInfo2.field : null;
    this.buttonSave = React.createRef();
    this.state = {
      additionalData: address.additionalData,
      addressType: address.addressType || null,
      apartment: address.apartment || null,
      bis: address.bis || false,
      coordinates: address.coordinates || null,
      coordinatesApproximated: false,
      corner1: address.firstCorner || null,
      corner2: address.secondCorner || null,
      department: (address.department || {}).id || null,
      doorNumber: address.doorNumber || null,
      extraInfoType1: extraInfoField1 ? extraInfoField1.id : '',
      extraInfoType2: extraInfoField2 ? extraInfoField2.id : '',
      extraInfoLine1: extraInfo1 ? extraInfo1.value : '',
      extraInfoLine2: extraInfo2 ? extraInfo2.value : '',
      letter: address.letter || null,
      location: (address.cityName || {}).id || null,
      mapCoordinatesLoaded: true,
      // mapCoordinatesLoaded: !!props.address || true,
      shouldConfirmStreet: false,
      street: address.streetName || null,
      suggestedStreet: null,
      useUserSelectedMarker: false,
      promptCheck: false,
      showDeleteModal: false,
      modalForbidTextTitle: '',
      modalForbidTextContent: '',
    };
    // We want to store user set coordinates separately to have both
    this._userSetCoordinates = null;
    this._onMapUpdate = this._onMapUpdate.bind(this);
    this._onMapUpdateV2 = this._onMapUpdateV2.bind(this);
    this._createAddress = this._createAddress.bind(this);
    this._toggleUseUserSelectedMapMarker = this._toggleUseUserSelectedMapMarker.bind(this);
    this._queryForAddress = this._queryForAddress.bind(this);
    this._updateAdditionalData = this._updateAdditionalData.bind(this);
    this._updateAddressType = this._updateAddressType.bind(this);
    this._updateApartment = this._updateApartment.bind(this);
    this._toggleBis = this._toggleBis.bind(this);
    this._updateCorner1 = this._updateCorner1.bind(this);
    this._updateCorner2 = this._updateCorner2.bind(this);
    this._updateDepartment = this._updateDepartment.bind(this);
    this._updateDoorNumber = this._updateDoorNumber.bind(this);
    this._updateExtraInfoLine1 = this._updateExtraInfoLine1.bind(this);
    this._updateExtraInfoLine2 = this._updateExtraInfoLine2.bind(this);
    this._updateExtraInfoType1 = this._updateExtraInfoType1.bind(this);
    this._updateExtraInfoType2 = this._updateExtraInfoType2.bind(this);
    this._updateLetter = this._updateLetter.bind(this);
    this._updateLocation = this._updateLocation.bind(this);
    this._updateStreet = this._updateStreet.bind(this);
    this._editAddress = this._editAddress.bind(this);
    this._deleteAddress = this._deleteAddress.bind(this);
    this._acceptStreetSuggestion = this._acceptStreetSuggestion.bind(this);
    this._cancelStreetSuggestion = this._cancelStreetSuggestion.bind(this);

    this._showDeleteModal = this._showDeleteModal.bind(this);
    this._closeDeleteModal = this._closeDeleteModal.bind(this);
    this._toggleErrorButton = this._toggleErrorButton.bind(this);
    this._toggleSuccessButton = this._toggleSuccessButton.bind(this);

    this._closeModalChangeLocality = this._closeModalChangeLocality.bind(this);
    this._createOrEditAddressModal = this._createOrEditAddressModal.bind(this);
    this._beforeSubmitForm = this._beforeSubmitForm.bind(this);
    this._closeModalCheks = this._closeModalCheks.bind(this);
    this._onAcceptCheckRequest = this._onAcceptCheckRequest.bind(this);
  }

  componentDidMount() {
    if (this.state.department) {
      LocationStore.getLocations(this.state.department);
    }
  }

  componentWillUnmount() {
    LocationStore.clearLocations();
  }

  _getCommonAddressFields() {
    const selectedAddressType = ADDRESS_TYPE_OPTIONS.find(addressType => (
      addressType.value === this.state.addressType
    ));
    return {
      additionalData: this.state.additionalData,
      addressType: selectedAddressType.label,
      apartment: this.state.apartment,
      bis: this.state.bis,
      coordinates: this.state.coordinatesApproximated
        ? null
        : this.state.coordinates,
      corner1: this.state.corner1,
      corner2: this.state.corner2,
      department: this.state.department,
      doorNumber: this.state.doorNumber || 0,
      extraInfoType1: this.state.extraInfoType1,
      extraInfoType2: this.state.extraInfoType2,
      extraInfoLine1: this.state.extraInfoLine1,
      extraInfoLine2: this.state.extraInfoLine2,
      letter: this.state.letter,
      location: this.state.location,
      street: this.state.street,
      userId: SessionStore.user.id,
      // If no user coordinates have been set, use the coordinates.
      // This makes sense when the location is approximated and the user didn't move the map
      userSetCoordinates: this._userSetCoordinates || this.state.coordinates,
    };
  }

  _createOrEditAddressModal() {
    if (this.props.address && this.props.address.id) {
      this.setState({
        location: Number(AddressStore.editionError.message),
      }, async () => {
        await this._editAddress();
      });
    } else {
      this.setState({
        location: Number(AddressStore.creationError.message),
      }, async () => {
        await this._createAddress();
      });
    }
  }

  _closeModalCheks() {
    this.setState({
      promptCheck: false,
    });
  }

  _beforeSubmitForm(event) {
    event.preventDefault();
    const additionalData = this.state.additionalData ? this.state.additionalData.toLowerCase() : '';
    const verifyCheck = additionalData.includes('u$s') || additionalData.includes('usd')
      || additionalData.includes('dólares') || additionalData.includes('dólar')
      || additionalData.includes('dolares') || additionalData.includes('dolar')
      || additionalData.includes('cheque') || additionalData.includes('cheques');
    const verifyHour = additionalData.includes('horario') || additionalData.includes('horarios')
      || additionalData.includes('hora');
    if (verifyCheck || verifyHour) {
      let text = '';
      if (verifyCheck && verifyHour) {
        text = 'cheques u horarios';
      } else {
        text = verifyCheck ? 'cheques' : 'horarios';
      }
      this.setState({
        modalForbidTextTitle: `Recuerde que no debe incluir nada sobre ${text} en el campo de ampliación`,
        modalForbidTextContent: '¿Desea continuar?',
        promptCheck: true,
      });
    } else {
      if (this.props.address === null) {
        this._createAddress();
        return;
      }
      this._editAddress();
    }
  }

  _onAcceptCheckRequest() {
    this.setState({
      promptCheck: false,
    });
    if (this.props.address === null) {
      this._createAddress();
      return;
    }
    this._editAddress();
  }

  async _createAddress() {
    AddressStore.clearCreationAndEditionErrors();
    const newAddressId = await AddressStore.createAddress(this._getCommonAddressFields());
    if (!AddressStore.creationError) {
      this.props.onAddressCreation(newAddressId);
    }
  }

  async _editAddress() {
    AddressStore.clearCreationAndEditionErrors();
    const commonFields = this._getCommonAddressFields();
    const updatedAddressId = await AddressStore.editAddress({
      ...commonFields,
      id: this.props.address.id,
    });
    if (!AddressStore.editionError) {
      this.props.onAddressEdition(updatedAddressId);
    }
  }

  async _queryForAddress(event) {
    event.preventDefault();
    AddressStore.clearCreationAndEditionErrors();

    // The door number we send to the API must be either the explicit
    // door number the user wrote, or the block ID in case the user has
    // specified it on the extra info fields and the block ID is a numeric type.
    let { doorNumber } = this.state;
    if (this.state.extraInfoType1 === BLOCK_PARAM_ID &&
      this.state.extraInfoLine1 && /^\d*$/.test(this.state.extraInfoLine1)) {
      doorNumber = this.state.extraInfoLine1;
    }
    if (this.state.extraInfoType2 === BLOCK_PARAM_ID &&
      this.state.extraInfoLine2 && /^\d*$/.test(this.state.extraInfoLine2)) {
      doorNumber = this.state.extraInfoLine2;
    }
    let coordinates = null;
    let coordinatesApproximated = false;
    try {
      const response = await AddressController.determineAddress(
        this.state.street,
        doorNumber || 0,
        LocationStore.locations.find(location => (
          this.state.location === location.id
        )).name,
        LocationStore.departments.find(department => (
          this.state.department === department.id
        )).name,
      );
      if (!response.error || response.approximated) {
        ({ coordinates } = response);
      }
      if (!response.error && !response.approximated) {
        const shouldCheckStreet = !!(
          response.street &&
          response.street.name &&
          response.street.id
        );
        if (shouldCheckStreet) {
          this.setState((prevState) => {
            const shouldConfirmStreet = shouldCheckStreet &&
              prevState.street !== response.street.name;
            return {
              shouldConfirmStreet,
              suggestedStreet: shouldConfirmStreet ? response.street.name : null,
            };
          });
        }
      }
      coordinatesApproximated = response.approximated;
    } catch (error) {
      coordinates = null;
    }
    this.setState((prevState) => {
      // NOTE: if the coordinates exist and are exactly the same as the ones stored
      // it means that the user has changed the address details without changing
      // the predicted address. The coordinates are artificially changed just enough
      // so that the map re-renders, but the user sees no error on the location.
      if (coordinates &&
        prevState.coordinates &&
        prevState.coordinates.latitude === coordinates.latitude &&
        prevState.coordinates.longitude === coordinates.longitude
      ) {
        coordinates.latitude += COORDINATES_NUDGE_VALUE;
      }
      return {
        coordinates,
        coordinatesNotFound: !coordinates,
        coordinatesApproximated: coordinates && coordinatesApproximated,
        mapCoordinatesLoaded: true,
      };
    });
    this._userSetCoordinates = null;
  }

  _canSubmitAddress() {
    return !AddressStore.loadingCreation &&
      !AddressStore.loadingEdition &&
      this._dataIsValid() &&
      this.state.mapCoordinatesLoaded;
  }

  /* canSubmitAddress() {
    return !AddressStore.loadingCreation &&
      !AddressStore.loadingEdition &&
      this._dataIsValid() &&
      this.state.mapCoordinatesLoaded &&
      (
        (this.state.coordinates && !this.state.coordinatesApproximated) || (
          this.state.useUserSelectedMarker
        )
      );
  } */

  _dataIsValid() {
    const additionalInfoSatisfied = true;
    // If the department is not Montevideo, then additional info must be provided
    // if (this.state.department !== MONTEVIDEO_ID) {
    //   additionalInfoSatisfied = this.state.additionalData;
    // }
    return this.state.addressType &&
      additionalInfoSatisfied &&
      this.state.department &&
      this.state.corner1 &&
      this.state.location &&
      this.state.street;
  }

  _processSuggestionLocation(location, locationId) {
    if (!location || !locationId) {
      return;
    }
    const { locations } = LocationStore;
    // If the location is unknown, add it to the store as if it had always been there.
    if (!locations || !locations.find(loc => loc.id === locationId)) {
      LocationStore.addLocation(locationId, location);
    }
    // Select the location
    this.setState({
      location: locationId,
    });
  }

  _onAddressDataChange() {
    AddressStore.clearCreationAndEditionErrors();
    this.setState({
      // mapCoordinatesLoaded: false,
      mapCoordinatesLoaded: true,
    });
  }

  _onDepartmentOrLocationChange() {
    this.setState({
      street: null,
      corner1: null,
      corner2: null,
    });
  }

  _updateAdditionalData(event) {
    const { target: { value } } = event;
    this.setState({
      additionalData: (value || ''),
    });
  }

  _updateAddressType(addressType) {
    this.setState({
      addressType,
    });
    this._onAddressDataChange();
  }

  _updateApartment(event) {
    const { target: { value } } = event;
    if (value && value.length > MAX_APARTMENT_LENGTH) {
      return;
    }
    this.setState({
      apartment: value,
    });
    this._onAddressDataChange();
  }

  _toggleBis() {
    this.setState(prevState => ({
      bis: !prevState.bis,
    }));
  }

  _updateCorner1(street) {
    this.setState({
      corner1: (street || '').slice(0, MAX_STREET_LENGTH),
    });
    this._onAddressDataChange();
  }

  _updateCorner2(street) {
    this.setState({
      corner2: (street || '').slice(0, MAX_STREET_LENGTH),
    });
    this._onAddressDataChange();
  }

  _updateDepartment(department) {
    this.setState({
      department,
    });
    LocationStore.getLocations(department);
    this._onAddressDataChange();
    this._onDepartmentOrLocationChange();
  }

  _updateDoorNumber(event) {
    const { target: { value } } = event;
    this.setState({
      doorNumber: (value || '').replace(/\D/, ''),
    });
    this._onAddressDataChange();
  }

  _updateExtraInfoType1(value) {
    this.setState({
      extraInfoType1: value,
    });
    this._onAddressDataChange();
  }

  _updateExtraInfoType2(value) {
    this.setState({
      extraInfoType2: value,
    });
    this._onAddressDataChange();
  }

  _updateExtraInfoLine1(event) {
    const { target: { value } } = event;
    this.setState({
      extraInfoLine1: (value || '').slice(0, MAX_EXTRA_INFO_LENGTH),
    });
    this._onAddressDataChange();
  }

  _updateExtraInfoLine2(event) {
    const { target: { value } } = event;
    this.setState({
      extraInfoLine2: (value || '').slice(0, MAX_EXTRA_INFO_LENGTH),
    });
    this._onAddressDataChange();
  }

  _updateLetter(event) {
    const { target: { value } } = event;
    if (value && value.length > MAX_LETTER_LENGTH) {
      return;
    }
    this.setState({
      letter: value,
    });
    this._onAddressDataChange();
  }

  _updateLocation(locationId) {
    this.setState({
      location: locationId,
    });
    this._onAddressDataChange();
    this._onDepartmentOrLocationChange();
  }

  _updateStreet(street, location, locationId) {
    this._processSuggestionLocation(location, locationId);
    this.setState({
      street: (street || '').slice(0, MAX_STREET_LENGTH),
    });
    this._onAddressDataChange();
  }

  _onMapUpdate({ center }) {
    // NOTE: when initially rendering the map on a location, this method is fired
    // but the coordinates passed are very slightly different that the ones set initially.
    // That's why checking for exact equality isn't enough, and a radius of equality is determined
    // by MINIMUM_COORDINATE_DIFFERENCE.
    if (!this.state.coordinates ||
      Math.abs(center.lat - this.state.coordinates.latitude) > MINIMUM_COORDINATE_DIFFERENCE ||
      Math.abs(center.lng - this.state.coordinates.longitude) > MINIMUM_COORDINATE_DIFFERENCE) {
      this._userSetCoordinates = {
        latitude: center.lat,
        longitude: center.lng,
      };
    } else {
      this._userSetCoordinates = null;
    }
  }

  _onMapUpdateV2({ lat, lng }) {
    this._userSetCoordinates = {
      latitude: lat,
      longitude: lng,
    };
  }

  _toggleUseUserSelectedMapMarker() {
    this.setState(prevState => ({
      useUserSelectedMarker: !prevState.useUserSelectedMarker,
    }));
  }

  _showDeleteModal(event) {
    event.preventDefault();
    this.setState({
      showDeleteModal: true,
    });
  }

  _closeDeleteModal(event) {
    event.preventDefault();
    this.setState({
      showDeleteModal: false,
    });
  }

  _toggleErrorButton(event) {
    event.preventDefault();
    this.setState({
      showDeleteModal: false,
    });

    AddressStore.clearDeletionErrors();
  }

  _toggleSuccessButton(event) {
    event.preventDefault();
    this.setState({
      showDeleteModal: false,
    });

    AddressStore.clearDeletionSuccess();

    this.props.onAddressEdition();
  }

  _deleteAddress(event) {
    event.preventDefault();
    AddressStore.deleteAddress(this.props.address.id);
  }

  _acceptStreetSuggestion() {
    this.setState(prevState => ({
      street: prevState.suggestedStreet,
      suggestedStreet: null,
      shouldConfirmStreet: false,
    }));
  }

  _cancelStreetSuggestion() {
    this.setState({
      suggestedStreet: null,
      shouldConfirmStreet: false,
    });
  }

  _closeModalChangeLocality() {
    console.log(this);
    AddressStore.clearCreationAndEditionErrors();
  }

  render() {
    const extraInfoFields = ConfigParametersStore.parameters.addressExtraInfoFields;
    const extraInfoOptions = extraInfoFields.map(field => ({
      label: field.value,
      value: field.id,
    }));
    let extraInfoType1 = extraInfoOptions.find(option => (
      option.value === this.state.extraInfoType1
    ));
    if (extraInfoType1) {
      extraInfoType1 = extraInfoType1.label;
    }
    let extraInfoType2 = extraInfoOptions.find(option => (
      option.value === this.state.extraInfoType2
    ));
    if (extraInfoType2) {
      extraInfoType2 = extraInfoType2.label;
    }
    const departmentOptions = LocationStore.departments.map(departmentData => ({
      value: departmentData.id,
      label: departmentData.name,
    }));
    const montevideoIsSelected = this.state.department === MONTEVIDEO_ID;
    const selectedDepartment = departmentOptions.find(department => (
      department.value === this.state.department
    ));
    const locationOptions = (LocationStore.locations || []).map(locationData => ({
      value: locationData.id,
      label: locationData.name,
    }));
    const selectedLocation = locationOptions.find(location => (
      location.value === this.state.location
    ));
    const selectedAddressType = ADDRESS_TYPE_OPTIONS.find(addressType => (
      addressType.value === this.state.addressType
    ));
    const streetError = this.state.street === '' ? 'Este campo no puede ser vacío' : null;
    const corner1Error = this.state.corner1 === '' ? 'Este campo no puede ser vacío' : null;

    const localityAfterError = this.props.address && this.props.address.id ?
      locationOptions.find(item =>
        Number(AddressStore.editionError && AddressStore.editionError.message) ===
        Number(item.value)) :
      locationOptions.find(item =>
        Number(AddressStore.creationError && AddressStore.creationError.message) ===
        Number(item.value));
    return (
      <div ref={this.props.reference}>
        {
          !this.props.withoutTitle &&
          <h2 className={styles.title}>
            Nueva dirección
          </h2>
        }
        {
          this.state.promptCheck &&
          <SweetAlert
            title={this.state.modalForbidTextTitle}
            showCancel
            confirmBtnText="Sí"
            confirmBtnCssClass={`${styles.btnAccept} ${styles.btn}`}
            cancelBtnText="No"
            cancelBtnCssClass={`${styles.btnNoAccept} ${styles.btn}`}
            onConfirm={this._onAcceptCheckRequest}
            onCancel={this._closeModalCheks}
            closeOnClickOutside={false}
            style={{
              width: '100%',
              left: 0,
              right: 0,
              maxWidth: '500px',
              margin: '0 auto',
              top: '30%',
            }}
          >
            <p>{this.state.modalForbidTextContent}</p>
          </SweetAlert>
        }
        <form
          className={[styles.form, this.props.collapseEarly ? styles.earlyCollapse : ''].join(' ')}
          onSubmit={this._beforeSubmitForm}
        >
          <div className={styles.inputSection}>
            <div className={styles.selectContainer}>
              <label className={styles.label}>
                Tipo *
              </label>
              <Select
                className={styles.select}
                onChange={this._updateAddressType}
                options={ADDRESS_TYPE_OPTIONS}
                value={selectedAddressType ? selectedAddressType.label : 'Seleccionar'}
              />
            </div>
            <div className={styles.selectGroup}>
              <div
                className={styles.selectGroupContainer}
              >
                <label className={styles.label}>
                  Departamento *
                </label>
                <Select
                  className={styles.select}
                  onChange={this._updateDepartment}
                  options={departmentOptions}
                  value={selectedDepartment ? selectedDepartment.label : 'Seleccionar'}
                />
              </div>
              <div
                className={styles.selectGroupContainer}
              >
                <label className={styles.label}>
                  Localidad *
                </label>
                <Select
                  className={styles.select}
                  disabled={!LocationStore.locations}
                  onChange={this._updateLocation}
                  options={locationOptions}
                  value={selectedLocation ? selectedLocation.label : 'Seleccionar'}
                />
              </div>
            </div>
            <StreetAutocompleteInput
              className={styles.input}
              currentDepartment={selectedDepartment ? selectedDepartment.label : null}
              currentLocation={selectedLocation ? selectedLocation.label : null}
              disabled={!this.state.department || !this.state.location}
              error={streetError}
              onChange={this._updateStreet}
              label="Calle *"
              placeholder="Solo calle, sin número de puerta"
              name="street"
              value={this.state.street || ''}
            />
            <div className={styles.inputGroup}>
              <BasicInput
                className={styles.input}
                label="N° de puerta"
                name="doorNumber"
                placeholder="Si no tiene N°, poner 0"
                onChange={this._updateDoorNumber}
                value={this.state.doorNumber || ''}
              />
              <div>
                <div>
                  <label className={styles.label}>
                    Bis
                  </label>
                </div>
                <Checkbox
                  checked={this.state.bis}
                  className={styles.bisCheckbox}
                  onClick={this._toggleBis}
                />
              </div>
              <BasicInput
                className={styles.input}
                label="Letra"
                name="letter"
                onChange={this._updateLetter}
                value={this.state.letter || ''}
              />
              <BasicInput
                className={styles.input}
                label="Apto"
                name="apartment"
                onChange={this._updateApartment}
                value={this.state.apartment || ''}
              />
            </div>
            <StreetAutocompleteInput
              className={styles.input}
              currentDepartment={selectedDepartment ? selectedDepartment.label : null}
              currentLocation={selectedLocation ? selectedLocation.label : null}
              disabled={!this.state.department || !this.state.location}
              error={corner1Error}
              onChange={this._updateCorner1}
              label="Primera esquina *"
              name="corner1"
              value={this.state.corner1 || ''}
            />
            <StreetAutocompleteInput
              className={styles.input}
              currentDepartment={selectedDepartment ? selectedDepartment.label : null}
              currentLocation={selectedLocation ? selectedLocation.label : null}
              disabled={!this.state.department || !this.state.location || !this.state.corner1}
              onChange={this._updateCorner2}
              label="Segunda esquina"
              name="corner2"
              value={this.state.corner2 || ''}
            />
            <div>
              <label className={styles.label}>
                Ampliación (si corresponde)
              </label>
              <div className={styles.multiSelectContainer}>
                <Select
                  className={styles.select}
                  onChange={this._updateExtraInfoType1}
                  options={extraInfoOptions}
                  value={extraInfoType1 || 'Seleccionar'}
                />
                <BasicInput
                  className={styles.noMarginInput}
                  disabled={!extraInfoType1}
                  label=""
                  name="extraInfoLine1"
                  onChange={this._updateExtraInfoLine1}
                  value={this.state.extraInfoLine1}
                />
                <Select
                  className={styles.select}
                  onChange={this._updateExtraInfoType2}
                  options={extraInfoOptions}
                  value={extraInfoType2 || 'Seleccionar'}
                />
                <BasicInput
                  className={styles.noMarginInput}
                  disabled={!extraInfoType2}
                  label=""
                  name="extraInfoLine2"
                  onChange={this._updateExtraInfoLine2}
                  value={this.state.extraInfoLine2}
                />
              </div>
            </div>
            <div className={styles.textareaContainer}>
              <label className={styles.label}>
                {/* If the department is not Montevideo, then this field is mandatory */}
                Información adicional{montevideoIsSelected ? '' : ''}
              </label>
              <span className={styles.label} style={{ marginLeft: 5, fontWeight: 'normal' }}>
                (solo datos del domicilio, por favor no incluya horarios en este campo)
              </span>
              <div className={styles.helpAdditionalInfo}>Ejemplo:
                <br />
                1.- CARACTERÍSTICAS DE LA CASA:
                <ul className={styles.listfeatures}>
                  <li>Pintada de [color]</li>
                  <li>Techo de tejas, planchada o chapa</li>
                  <li>[Cantidad] pisos</li>
                </ul>
                2.- UBICACIÓN:
                <ul className={styles.listfeatures}>
                  <li>Al lado o en frente de almacén, kiosko</li>
                  <li>Más cerca de [nombre de esquina]</li>
                  <li>En medio de la cuadra</li>
                </ul>
              </div>
              <textarea
                maxLength="300"
                className={styles.textarea}
                onChange={this._updateAdditionalData}
                value={this.state.additionalData || ''}
              />
            </div>
            <div>
              {
                !this._dataIsValid() &&
                <p className={styles.warningMessage}>
                  Faltan campos obligatorios por llenar
                </p>
              }
              {
                ((!!AddressStore.creationError &&
                AddressStore.creationError.code === ERROR_CODES.CHECKOUT_INVALID_LOCALITY
                && localityAfterError) || (!!AddressStore.editionError &&
                AddressStore.editionError.code === ERROR_CODES.CHECKOUT_INVALID_LOCALITY
                && localityAfterError)) &&
                <SweetAlert
                  title="Localidad incorrecta"
                  showCancel
                  confirmBtnText="Si"
                  confirmBtnCssClass={`${styles.btnAccept} ${styles.btn}`}
                  cancelBtnText="No"
                  cancelBtnCssClass={`${styles.btnNoAccept} ${styles.btn}`}
                  placeHolder="Write something"
                  onConfirm={this._createOrEditAddressModal}
                  onCancel={this._closeModalChangeLocality}
                  closeOnClickOutside={false}
                  style={{
                    width: '100%',
                    left: 0,
                    right: 0,
                    maxWidth: '500px',
                    margin: '0 auto',
                    top: '30%',
                  }}
                >
                  <p>
                    El punto en el mapa seleccionado para nosotros
                    se encuentra en la localidad {localityAfterError.label}.
                    ¿Desea cambiarla la localidad para poder guardar la información?
                  </p>
                </SweetAlert>
              }
              {
                AddressStore.creationError &&
                AddressStore.creationError.code &&
                <p className={styles.error}>
                  {/* eslint-disable-next-line no-nested-ternary */}
                  {ERROR_CODES_MESSAGE[AddressStore.creationError.code] ?
                      ERROR_CODES_MESSAGE[AddressStore.creationError.code] :
                      AddressStore.creationError.message ?
                          AddressStore.creationError.message :
                          'Ha ocurrido un error, por favor contactá con nuestra Atención al Cliente al 29027737'}
                </p>
              }
              {
                !this.props.address &&
                <div className={styles.buttonContainer}>
                  <PrimaryButton
                    className={styles.newButton}
                    disabled={!this._canSubmitAddress()}
                  >
                    Agregar Dirección
                  </PrimaryButton>
                </div>
              }
              {
                !!this.props.address &&
                <div className={styles.editButtonContainer}>
                  <SecondaryButton
                    className={styles.saveButton}
                    disabled={!this._canSubmitAddress()}
                    ref={this.buttonSave}
                  >
                    Guardar
                  </SecondaryButton>
                  <BaseButton
                    className={styles.deleteButton}
                    onClick={event => this._showDeleteModal(event)}
                  >
                    Borrar dirección
                  </BaseButton>
                </div>
              }
            </div>
          </div>
        </form>
        {
          this.state.shouldConfirmStreet &&
          this.state.suggestedStreet &&
          <Modal>
            <div className={styles.streetSuggestionModal}>
              <p className={styles.text}>
                El nombre de la calle que seleccionaste no coincide con el de nuestra
                base de datos, ¿querés cambiarlo por {this.state.suggestedStreet}?
              </p>
              <div className={styles.modalButtonContainer}>
                <SecondaryButton
                  type="button"
                  onClick={this._acceptStreetSuggestion}
                >
                  Aceptar
                </SecondaryButton>
                <SimpleButton
                  type="button"
                  onClick={this._cancelStreetSuggestion}
                >
                  Cancelar
                </SimpleButton>
              </div>
            </div>
          </Modal>
        }
        {
          this.state.showDeleteModal &&
          <Modal>
            <div className={styles.modalChildren}>
              {
                AddressStore.deletionError &&
                <div>
                  <p className={styles.modalError}>
                    Ha ocurrido un error, por favor intente nuevamente.
                  </p>
                  <SimpleButton
                    onClick={this._toggleErrorButton}
                    className={styles.messageModalButton}
                  >
                    Volver
                  </SimpleButton>
                </div>
              }
              {
                AddressStore.deletionInternalError &&
                <div>
                  <p className={styles.modalError}>
                    Estamos experimentando algunas dificultades en nuestro sitio.
                    Por favor intente nuevamente en unos momentos.
                  </p>
                  <SimpleButton
                    onClick={this._toggleErrorButton}
                    className={styles.messageModalButton}
                  >
                    Volver
                  </SimpleButton>
                </div>
              }
              {
                AddressStore.deletionSuccess &&
                <div>
                  <p className={styles.modalSuccess}>
                    Dirección eliminada con éxito
                  </p>
                  <SimpleButton
                    onClick={this._toggleSuccessButton}
                    className={styles.messageModalButton}
                  >
                    Volver
                  </SimpleButton>
                </div>
              }
              {
                AddressStore.deletingAddress &&
                <Spinner className={styles.modalSpinner} />
              }
              {
                !AddressStore.deletionInternalError &&
                !AddressStore.deletionError &&
                !AddressStore.deletingAddress &&
                !AddressStore.deletionSuccess &&
                <div>
                  <p className={styles.modalQuestion}>
                    ¿Está seguro que desea borrar esta dirección?
                  </p>
                  <p className={styles.modalText}>
                    Esta operación no puede deshacerse.
                  </p>
                  <div className={styles.modalButtonsSection}>
                    <SimpleButton
                      className={styles.hideModalButton}
                      onClick={this._closeDeleteModal}
                    >
                      Cancelar
                    </SimpleButton>
                    <SimpleButton
                      className={styles.deleteModalButton}
                      onClick={this._deleteAddress}
                    >
                      Borrar dirección
                    </SimpleButton>
                  </div>
                </div>
              }
            </div>
          </Modal>
        }
      </div>
    );
  }
}

AddressForm.propTypes = IPropTypes;
AddressForm.defaultProps = defaultProps;

const ObservedAddressForm = enforceDepartments(observer(AddressForm));

export { ObservedAddressForm as AddressForm };



// WEBPACK FOOTER //
// ./src/common/address-form/AddressForm.jsx